/**
 * Prijsaanvragen vanuit de configurator naar /api/pricing/calculate.
 * Eigenaar: prijzen.
 *
 * - Een prijstabel (aantallen x levertijden) gaat als een batch in een
 *   aanvraag. Voorheen kostte elke rij een eigen aanvraag, en liep een klant
 *   die opties vergeleek na een handvol klikken tegen de limiet per IP aan
 *   (src/lib/pricing/public-price-guard.ts).
 * - Een 429 wordt een PriceRateLimitedError met de wachttijd uit Retry-After,
 *   zodat de configurator een tijdelijke melding toont en zelf opnieuw
 *   probeert, in plaats van "Ongeldige combinatie".
 */

/** Gelijk aan MAX_PRICING_CALCULATE_BATCH in public-price-guard.ts. */
export const PRICING_CALCULATE_BATCH_LIMIT = 36;

/** Langste wachttijd voordat de configurator het zelf opnieuw probeert. */
export const MAX_AUTO_RETRY_SECONDS = 60;

export class PriceRateLimitedError extends Error {
  readonly retryAfterSeconds: number | null;

  constructor(retryAfterSeconds: number | null) {
    super('Te veel prijsaanvragen');
    this.name = 'PriceRateLimitedError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export function isPriceRateLimitedError(error: unknown): error is PriceRateLimitedError {
  return error instanceof PriceRateLimitedError || (error as { name?: string } | null)?.name === 'PriceRateLimitedError';
}

/** Retry-After in seconden (getal of HTTP-datum); null als het ontbreekt of onleesbaar is. */
export function parseRetryAfter(value: string | null | undefined, now = Date.now()): number | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Math.max(1, Number(trimmed));
  const date = Date.parse(trimmed);
  if (Number.isFinite(date)) return Math.max(1, Math.ceil((date - now) / 1000));
  return null;
}

/**
 * Hoe lang de configurator wacht voor een nieuwe poging. Een minuutlimiet
 * geeft hooguit 60 seconden; de daglimiet geeft uren, en dan proberen we het
 * na een minuut toch opnieuw (een geweigerde aanvraag telt niet mee).
 */
export function autoRetryDelaySeconds(retryAfterSeconds: number | null) {
  if (retryAfterSeconds === null || !Number.isFinite(retryAfterSeconds)) return 10;
  return Math.min(MAX_AUTO_RETRY_SECONDS, Math.max(1, Math.ceil(retryAfterSeconds)));
}

/** Gooit PriceRateLimitedError bij een 429; anders gebeurt er niets. */
export function throwIfRateLimited(response: Pick<Response, 'status' | 'headers'>) {
  if (response.status === 429) throw new PriceRateLimitedError(parseRetryAfter(response.headers.get('Retry-After')));
}

export type PricingCalculateItem = {
  productId: string;
  configuration: Record<string, unknown>;
  quantity: number;
  countryCode: string;
  locale: string;
  printfulPlacements?: string[];
};

export type PricingCalculateResult = { ok: boolean; status: number; data: any };

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Prijst meerdere samenstellingen van hetzelfde product. Een uitkomst per
 * item, in dezelfde volgorde; een mislukt item is { ok: false }. Een 429
 * gooit PriceRateLimitedError voor de hele batch.
 */
export async function fetchPricingBatch(
  items: PricingCalculateItem[],
  options: { signal?: AbortSignal; fetchImpl?: FetchLike } = {},
): Promise<PricingCalculateResult[]> {
  if (items.length === 0) return [];
  const fetchImpl: FetchLike = options.fetchImpl || ((input, init) => fetch(input, init));
  const chunks: PricingCalculateItem[][] = [];
  for (let i = 0; i < items.length; i += PRICING_CALCULATE_BATCH_LIMIT) {
    chunks.push(items.slice(i, i + PRICING_CALCULATE_BATCH_LIMIT));
  }
  const chunkResults = await Promise.all(chunks.map(async (chunk) => {
    const response = await fetchImpl('/api/pricing/calculate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ batch: chunk }),
      signal: options.signal,
    });
    throwIfRateLimited(response);
    const payload = await response.json().catch(() => null);
    const results: unknown[] = Array.isArray(payload?.results) ? payload.results : [];
    return chunk.map((_, index): PricingCalculateResult => {
      const result = results[index] as { status?: number; data?: unknown } | undefined;
      const status = Number(result?.status || (response.ok ? 500 : response.status) || 500);
      return { ok: status >= 200 && status < 300, status, data: result?.data ?? null };
    });
  }));
  return chunkResults.flat();
}
